import React from 'react'

import clsx from 'clsx'

import Grid from 'components/Grid'
import HoverEvent from 'components/HoverEvent'
import Icon from 'components/Icon'
import InfoCircleTooltip from 'components/InfoCircleTooltip'
import MultiColorProgressBar from 'components/MultiColorProgressBar'
import SnippetsAccordion from 'components/SnippetsAccordion'
import Tag from 'components/Tag'
import Tooltip from 'components/Tooltip'
import Typography from 'components/Typography'
import { EventContext } from 'context/EventContext'
import { formatRatingAverage } from 'helpers/formatters'

import Info from './components/Info'
import FeaturesBreakdownTooltip from '../FeaturesBreakdownTooltip/FeaturesBreakdownTooltip'
import FeatureWithRating from '../KeyFeatures/components/FeatureWithRating'
import KeyFeaturesHeading from '../KeyFeaturesHeading'

import classes from './ProductInfo.module.scss'

/** @type {import("./types").default} */
const ProductInfo = ({ listing, isFirstCard, hasKeyFeatures, className, facet, listingPosition, ...props }) => {
  const displayFeatures = listing?.key_features?.slice(0, 2) || []
  const allFeatures = listing?.key_features || []
  const enabledFeatures = listing?.key_features?.filter(feature => feature.global_feature_id).length
  const countFeatures = allFeatures.length

  return (
    <div className={clsx('ProductInfo', classes.root, className)} {...props}>
      <EventContext
        value={{
          engagementComponent: 'best-product_card-item',
        }}
      >
        <Info listing={listing} component="h2" listingPosition={listingPosition} />
        <div>
          <Grid container>
            <Grid item xs={12} lg={5}>
              <div className={classes.infoCircleContainer}>
                <Typography variant="heading-xs" component="span">
                  Reviews Sentiment
                </Typography>
                <HoverEvent
                  event={{
                    engagementDestination: 'show_reviews-sentiment-definition',
                    engagementDetails: 'hover_tool-tip',
                  }}
                >
                  <InfoCircleTooltip
                    title="The chart represents the distribution of positive, neutral and negative reviews."
                    enterTouchDelay={100}
                    leaveTouchDelay={3000}
                    className={classes.infoCircle}
                  />
                </HoverEvent>
              </div>
              <MultiColorProgressBar reviews={listing.reviews} listingId={listing.id} />
            </Grid>
            <Grid item xs={12} lg={2} className={classes.content} />
            {hasKeyFeatures && (
              <Grid item lg={5}>
                <KeyFeaturesHeading label={`${enabledFeatures}/${countFeatures}`} listing={listing} />
                <FeatureWithRating feature={displayFeatures[0]} />
                <FeatureWithRating feature={displayFeatures[1]} />

                <div className={classes.featuresLink}>
                  <FeaturesBreakdownTooltip features={allFeatures}>
                    <Typography className={classes.viewAllLink}>
                      See full list
                      <Icon className={classes.chevron} icon={['fas', 'chevron-down']} size="14" />
                    </Typography>
                  </FeaturesBreakdownTooltip>
                </div>
              </Grid>
            )}
          </Grid>
          <div className={classes.insights}>
            <Typography variant="heading-xs">User insights about the {facet.label.toLowerCase()} feature</Typography>

            <div className={classes.insightsTags}>
              <Tag color="grey" size="small" className={classes.ratingTag}>
                <Icon icon={['fas', 'star']} size="12" />
                {formatRatingAverage(listing.feature?.average_rating)}
              </Tag>

              {listing?.feature?.importance && (
                <HoverEvent
                  event={{
                    engagementGA4Name: 'engagement_hover',
                    engagementDestination: 'show_feature-importance-definition',
                    engagementDetails: 'hover_tool-tip',
                  }}
                >
                  <Tooltip
                    title={`This feature was rated as ${listing.feature.importance} by ${listing.feature.percentage_of_importance}% of ${listing.name}'s reviewers`}
                  >
                    <Tag color="grey" size="small">
                      {listing.feature.importance}
                    </Tag>
                  </Tooltip>
                </HoverEvent>
              )}
            </div>

            {listing?.feature && listing.feature?.summary}
          </div>
          <SnippetsAccordion snippets={listing?.feature?.snippets} defaultExpanded={isFirstCard} />
        </div>
      </EventContext>
    </div>
  )
}
export default ProductInfo
